import React, { useEffect, useState } from 'react';
import {
  Grid,
  Select,
  MenuItem,
  TextField,
  Divider,
  InputLabel,
  SelectChangeEvent,
  color,
  textSize,
  Typography
} from '@kis-technologies/kis-ui';
import { useTranslation } from 'react-i18next';
import { PhoneNumberProps, Country } from '../types';
import { useStyles } from '../styled';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { selectors as configSelectors } from 'services/configuration';
import InputMask from 'react-input-mask';
import { selectors as ticketingSelectors } from 'services/ticketing';
import countries from '../countries.json';

export const PhoneNumberInput = React.forwardRef<HTMLInputElement, PhoneNumberProps>((props, inputRef) => {
  const { t } = useTranslation();
  const { error, inputModel } = props;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const config = useSelector(configSelectors.selectConfiguration);
  const defaultCountryCodes: string[] = get(config, `${ticketingPath}.priorityPhoneCountryCodes`, []);
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');

  const [selectedCountry, setSelectedCountry] = useState<Country>(inputModel.selectedCountry || countries[0]);
  const [number, setNumber] = useState<string>(inputModel.keyboard);
  const [value, setValue] = useState<string>(
    inputModel.selectedCountry && inputModel.keyboard ? inputModel.selectedCountry.code + inputModel.keyboard : ''
  );
  const [defaultCountries, setDefaultCountries] = useState<Country[]>();
  const [filteredCountries, setFilteredCountries] = useState<Country[]>();
  const classes = useStyles({ fontFamily, error })();

  useEffect(() => {
    if (defaultCountryCodes.length > 0) {
      const filtered: Country[] = [];
      defaultCountryCodes.forEach((code) => {
        const matched = countries.find((country) => code === country.iso);
        if (matched) {
          // Filtered this way to maintain the configured order
          filtered.push(matched);
        }
      });
      if (filtered && filtered.length) {
        setFilteredCountries(countries.filter((country) => !defaultCountryCodes.includes(country.iso)));
        setDefaultCountries(filtered);
        if (!inputModel.selectedCountry) {
          setSelectedCountry(filtered[0]);
          setTimeout(() => {
            props.setPhoneNumber(value, { keyboard: number, selectedCountry: filtered[0] });
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // To handle keyboard clicks in UI
  useEffect(() => {
    if (selectedCountry && selectedCountry.size >= inputModel.keyboard.length) {
      setNumber(inputModel.keyboard);
    } else {
      // If user entered more digits
      props.setPhoneNumber(value, { keyboard: number, selectedCountry });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputModel]);

  // To handle number change in UI
  useEffect(() => {
    if (selectedCountry) {
      setValue(number ? selectedCountry.code + number : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number]);

  // To handle keyboard clicks in UI
  useEffect(() => {
    props.setPhoneNumber(value, { keyboard: number, selectedCountry });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onChangeCountry = (event: SelectChangeEvent<unknown>) => {
    const selected = countries.find((country) => country.iso === event.target.value);
    if (selected) {
      setSelectedCountry(selected);
      if (number) {
        setValue(selected.code + number);
      } else {
        props.setPhoneNumber(value, { keyboard: number, selectedCountry: selected });
      }
    }
  };

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 608,
        minWidth: 600,
        fontFamily
      }
    }
  };

  return (
    <Grid>
      <InputLabel className={classes.inputLabel}>{props.label}</InputLabel>
      <Grid className={classes.phoneNumberContainer} {...props.gridProps} data-phone-number-input-container>
        <Select
          labelId="PhoneNumberCountryCodeLabel"
          id="PhoneNumberCountryCode"
          label="CountryCode"
          className={classes.countryCode}
          disableUnderline
          onChange={onChangeCountry}
          variant="standard"
          value={selectedCountry && selectedCountry.iso}
          renderValue={() => (
            <MenuItem className={classes.selectedItem}>
              {selectedCountry && (
                <>
                  <img src={selectedCountry.flag} alt={selectedCountry.name} className={classes.selectedImage} />
                  {selectedCountry.code}
                </>
              )}
            </MenuItem>
          )}
          MenuProps={MenuProps}
          {...props.selectInputProps}
        >
          {defaultCountries &&
            defaultCountries.map((country) => {
              return (
                <MenuItem value={country.iso} className={classes.menuItem}>
                  <img src={country.flag} alt={country.name} className={classes.flagImage} />
                  {country.code} {country.name}
                </MenuItem>
              );
            })}
          <Divider />
          {filteredCountries &&
            filteredCountries.map((country) => {
              return (
                <MenuItem value={country.iso} className={classes.menuItem}>
                  <img src={country.flag} alt={country.name} className={classes.flagImage} />
                  {country.code} {country.name}
                </MenuItem>
              );
            })}
        </Select>
        {selectedCountry && (
          <InputMask
            mask={selectedCountry.mask}
            name="phoneNumberMask"
            value={number}
            className={classes.phoneNumber}
            placeholder={t('commonText.phoneNumber')}
            alwaysShowMask
            onFocus={props.onFocus}
            onClick={props.onClick}
          >
            <TextField
              size={'medium'}
              InputProps={{
                inputRef,
                disableUnderline: true
              }}
              {...props.textInputProps}
              variant="standard"
            />
          </InputMask>
        )}
      </Grid>
      {error && (
        <Typography color={color.RED} fontSize={textSize.kiosk.SMALL} sx={{ padding: '0 0 0 58px' }}>
          {error}
        </Typography>
      )}
    </Grid>
  );
});
